import { Icon, IconProps } from '@iconify/react';
import { forwardRef } from 'react';

/*
 Naming convention: 
  1. If the icon is from MUI, use the prefix 'mui-' & add icon element
  2. Use 1 word for the icon name, unless it already exists. 
  3. If the word already exists, use a descriptor post-fix:
    - '_filled' for filled/solid icons
    - '_outline' for outlined icons
    - '_circle' for circled icons
*/

export type IconDictionaryType = { [key: string]: string | JSX.Element };
export const IconDictionary: IconDictionaryType = {
  // Actions
  add: 'akar-icons:plus',
  subtract: 'akar-icons:minus',
  apply: 'tabler:arrow-up-right',
  add_circle: 'carbon:add-alt',
  back: 'eva:arrow-back-outline',
  bulk: 'ph:stack-duotone',
  close: 'eva:close-fill',
  close_outline: 'bi:x-circle',
  close_filled: 'material-symbols:cancel',
  copy: 'akar-icons:clipboard',
  clipboard: 'fluent:clipboard-12-regular',
  clipboard_done: 'solar:clipboard-check-linear',
  clipboard_pending: 'fluent:clipboard-more-24-regular',
  clipboard_failed: 'fluent:clipboard-error-24-regular',
  compare_arrows: 'material-symbols:compare-arrows-rounded',
  delete: 'clarity:trash-solid',
  delete_outline: 'clarity:trash-line',
  dock_left: 'bxs:dock-left',
  dock_right: 'bxs:dock-right',
  download: 'ix:download',
  duplicate: 'bx:duplicate',
  edit: 'octicon:pencil-24',
  edit_outlined: 'bi:pencil-square',
  edit_filled: 'mdi:pencil',
  external_link: 'tabler:external-link',
  hidden: 'ph:eye-closed',
  home: 'uiw:home',
  home_storage: 'material-symbols:home-storage',
  import: 'mingcute:file-import-line',
  link: 'ci:link',
  movie_search: 'mdi:movie-search-outline',
  minimize: 'proicons:arrow-minimize',
  maximize: 'proicons:arrow-maximize',
  window_collapse_right: 'system-uicons:window-collapse-right',
  pause: 'material-symbols:pause',
  pause_circle: 'gridicons:pause',
  play: 'mdi:play',
  play_circle: 'mdi:play-circle',
  play_outline: 'octicon:play-16',
  popup: 'entypo:popup',
  preview: 'ph:eye',
  refresh: 'material-symbols:cached-rounded',
  reset: 'radix-icons:reset',
  send: 'basil:send-solid',
  search: 'akar-icons:search',
  search_results: 'pajamas:search-results',
  search_folders: 'mdi:folder-search',
  star_checked: 'mdi:star-check-outline',
  swap: 'iconamoon:swap',
  undo: 'iconoir:undo-circle',
  upload: 'ix:upload',
  zoom_in: 'material-symbols:zoom-in',
  zoom_out: 'material-symbols:zoom-out',
  fullscreen: 'ic:round-fullscreen',
  fullscreen_exit: 'ic:round-fullscreen-exit',

  // Status Cards
  clock: 'mdi:clock-outline',
  timelapse: 'material-symbols:timelapse-outline',
  flight: 'material-symbols:flight',

  // Time
  calendar: 'mdi:calendar-blank',
  calendar_clock: 'mdi:calendar-clock',
  clock_future: 'ph:clock-clockwise-fill', // clockwise arrow
  clock_progress: 'mdi:progress-clock', // left-half has dotted lines

  // Files
  archive: 'bi:archive',
  assignment: 'material-symbols:assignment',
  cancel: 'ic:baseline-do-not-disturb-alt',
  cloud_check: 'mdi:cloud-check-variant',
  cloud_download: 'ic:round-cloud-download',
  cloud_link: 'fluent:cloud-link-20-regular',
  cloud_upload: 'material-symbols:cloud-upload',
  draft: 'ri:draft-line',
  file: 'mdi:file-document-outline',
  file_filled: 'mdi:file-document',
  file_import: 'mingcute:file-import-line',
  file_none: 'fluent:document-none-24-regular',
  files: 'ep:files', // can probably replace with 'files_outline'
  files_outline: 'icomoon-free:files-empty',
  folder: 'material-symbols:folder',
  folder_outline: 'material-symbols:folder-outline-rounded',
  folder_open: 'material-symbols:folder-open',
  folder_open_outline: 'material-symbols:folder-open-outline-rounded',
  folder_create: 'material-symbols:create-new-folder-outline',
  folded_paper: 'majesticons:paper-fold-text-line',
  form: 'ant-design:form-outlined',
  graph: 'mdi:graph-line',
  logout: 'ic:round-logout',
  save: 'bx:save',
  skip: 'bi:skip-end-fill',
  read_only: 'clarity:lock-solid',

  // Arrows
  curved_arrow_down: 'ph:arrow-bend-right-down-bold',
  curved_arrow_right: 'ph:arrow-bend-down-right-bold',
  curved_arrow_up: 'ph:arrow-bend-right-up-bold',
  down: 'ion:chevron-down',
  down_caret: 'fa-solid:caret-down',
  down_filled: 'bxs:down-arrow',
  left: 'material-symbols:chevron-left-rounded',
  left_arrow: 'mdi:arrow-left',
  right: 'material-symbols:chevron-right-rounded',
  right_arrow: 'mdi:arrow-right',
  right_double: 'fluent:chevron-double-right-16-filled',
  up: 'ion:chevron-up',
  up_double: 'bx:chevrons-up',
  expand: 'fluent:arrow-fit-16-regular',
  condense: 'fluent:arrow-fit-in-16-regular',
  arrow_bidirectional_left_right: 'fluent:arrow-bidirectional-left-right-16-regular',

  //Panels
  panel_open_right: 'material-symbols-light:right-panel-open',
  panel_open_left: 'material-symbols-light:left-panel-open',
  panel_close_right: 'material-symbols-light:right-panel-close',
  panel_close_left: 'material-symbols-light:left-panel-close',

  // General
  ascending: 'teenyicons:up-solid',
  cards: 'system-uicons:card-timeline',
  check: 'akar-icons:check',
  check_double: 'akar-icons:double-check',
  checkbox: 'bx-checkbox',
  checkbox_minus: 'bx-checkbox-minus',
  checkbox_checked: 'carbon:checkbox-checked-filled',
  checkbox_unchecked: 'carbon:checkbox',
  circle: 'akar-icons:circle',
  checklist: 'bi:list-check',
  columns: 'fluent:column-triple-24-filled',
  comment: 'fluent:comment-28-regular',
  customer_assignment: 'fluent:contact-card-group-24-filled',
  customers: 'mingcute:group-fill',
  data: 'clarity:storage-line',
  dark_mode: 'fluent:dark-theme-24-filled',
  day: 'mdi:brightness-7',
  descending: 'teenyicons:down-solid',
  documentation: 'clarity:book-line',
  draggable: 'fluent:drag-20-filled',
  email: 'ic:outline-email',
  empty: 'emojione-v1:heavy-minus-sign',
  filter: 'mdi:filter',
  filter_lines: 'ion:filter',
  grip: 'ic:round-drag-handle', // draggable indicator
  grid: 'healthicons:ui-menu-grid',
  grid_outline: 'ion:grid-outline',
  group: 'ri:group-fill',
  hash: 'eva:hash-fill',
  history: 'fluent:history-20-regular',
  inbox: 'iwwa:box',
  info: 'mdi:information-outline',
  info_filled: 'material-symbols:info',
  job: 'icon-park-outline:view-grid-detail',
  layers: 'carbon:overlay',
  loading: 'eos-icons:loading',
  lock_open: 'material-symbols:lock-open-outline',
  lock_closed: 'material-symbols:lock-outline',
  loading_dots: 'eos-icons:three-dots-loading',
  list: 'circum:grid-2-h',
  menu: 'charm:menu-hamburger',
  merge: 'eos-icons:merge',
  night: 'mdi:brightness-4',
  note: 'material-symbols:sticky-note-2',
  notifications: 'ph:bell',
  notifications_filled: 'ph:bell-fill',
  overflow: 'charm:menu-kebab',
  paintbrush: 'fluent:paint-brush-16-filled',
  priority: 'ic:baseline-bolt',
  profiles: 'ant-design:profile-outlined',
  question: 'bi:question-lg',
  question_filled: 'akar-icons:question-fill',
  question_outline: 'bi:question-circle',
  robot: 'fa-solid:robot',
  robot_outline: 'ri:robot-2-line',
  settings: 'carbon:settings',
  settings_filled: 'clarity:cog-solid',
  switch: 'mi:switch',
  submit: 'formkit:submit',
  suitcase: 'uil:suitcase',
  timer: 'mdi:timer',
  timer_outline: 'lets-icons:clock',
  thumbnail: 'carbon:thumbnail-1',
  user: 'ph:user-bold',
  user_circle: 'mdi:user-circle',
  unassigned: 'ri:user-unfollow-line',
  unassigned_group: 'ri:group-line',
  user_filled: 'ri:user-fill',
  user_filled_star: 'ri:user-star-fill',
  user_inactive: 'fa6-solid:user-slash',
  waving: 'mdi:human-greeting-variant',
  rocket_launch: 'material-symbols:rocket-launch',
  map: 'material-symbols:map-outline',
  subtitles: 'material-symbols:subtitles-outline-rounded',
  progress_star: 'mdi:progress-star',
  minus_circle: 'ic:outline-remove-circle-outline',
  pin: 'fluent:pin-24-regular',
  pin_off: 'fluent:pin-off-24-regular',
  textbox_settings: 'fluent:textbox-settings-24-filled',

  // Polaris-specific
  complete: 'bi:check-circle-fill',
  complete_outline: 'mdi:check-circle-outline',
  complete_duotone: 'ph:check-circle-duotone',
  component_based: 'mdi:button-pointer',
  component_based_bundle: 'mdi:button-pointer',
  components: 'radix-icons:component-1',
  error: 'ic:outline-report-problem',
  error_circle: 'mdi:warning-circle-outline',
  error_filled: 'bi:exclamation-circle-fill',
  hold: 'akar-icons:stop-fill',
  related_jobs: 'fluent-mdl2:relationship',
  pending: 'carbon:pending',
  pending_actions: 'material-symbols:pending-actions',
  pending_filled: 'ic:round-pending',
  star: 'carbon:star',
  star_filled: 'carbon:star-filled',
  subtitle: 'ic:round-subtitles',
  version_based: 'carbon:magic-wand-filled',
  queued: 'carbon:queued',
  package: 'icon-park-twotone:tree-diagram',
  version_based_bundle: 'carbon:magic-wand-filled',

  // Job Type Labels
  movie: 'tabler:movie',
  paper_plane: 'fa-solid:paper-plane',
  computer: 'material-symbols:computer',
  text: 'fluent:text-description-24-filled',
  fact_check: 'material-symbols:fact-check',
  audio: 'system-uicons:audio-wave',
  received: 'ri:folder-received-fill',
  video_library: 'material-symbols:video-library',
  split_hint: 'fluent:split-hint-20-filled',
  caption: 'carbon:closed-caption-alt',
  down_right: 'ph:arrow-square-down-right-fill',

  // File types
  image: 'bxs:image-alt',
  image_outline: 'teenyicons:image-outline',
  json: 'codicon:json',
  movie_roll: 'icon-park-solid:movie',
  pdf: 'ant-design:file-pdf-outlined',
  premier_pro: 'simple-icons:adobepremierepro',
  tv: 'bx:tv',
  video: 'majesticons:video',
  video_outline: 'mdi:video-outline',
  music: 'material-symbols:music-note',
  volume: 'eva:volume-down-fill', // FileTypeDisplay
  volume_up: 'eva:volume-up-fill',
  volume_mute: 'eva:volume-mute-fill',
  xml: 'mdi:xml',
  unknown: 'carbon:unknown',

  // File Ingest Icons
  ingest_audio: 'fluent:headphones-sound-wave-48-regular',
  ingest_doc: 'bi:filetype-doc',
  ingest_subtitle: 'ph:subtitles-thin',

  // Packaging
  packaging: 'fluent:box-edit-20-regular',
  assembly_template: 'fluent:box-32-regular',
  file_circle: 'pepicons-pop:file-circle-filled'
};

export interface PolarisIconProps extends IconProps {
  icon: Extract<keyof IconDictionaryType, string>;
}

const PolarisIcon = ({ icon, ...iconProps }: PolarisIconProps, ref: any) => {
  const iconDictionaryKeys = Object.keys(IconDictionary);

  // if the icon exists in the dictionary, use it; otherwise pass in string to Icon
  return iconDictionaryKeys.includes(icon) && typeof IconDictionary[icon] === 'string' ? (
    <Icon ref={ref as any} icon={IconDictionary[icon] as string} {...iconProps} />
  ) : (
    <Icon ref={(ref as any) || undefined} icon={icon} {...iconProps} />
  );
};

export default forwardRef(PolarisIcon);
